/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  emptyOf,
  entity,
  identity,
  optimisticLockingOf,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
  sortByIgnoreCase,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { countBy, first, fromPairs, invertBy, isEmpty, keys, mapKeys, mapValues, pickBy, values } from 'lodash';
import { Observable } from 'rxjs';
import { OfficeDetailsMapper } from './mappers/office-details.mapper';
import { OfficeFilterMapper, OfficeListItemMapper, OfficeActiveRecordMapper } from './mappers/office-list-item.mapper';
import { OfficeMapper } from './mappers/office.mapper';
import { OfficeDetailsDto, OfficeVariableSet } from './models/office-details.dto';
import { OfficeActiveRecordDto, OfficeFilterDto, OfficeListItemDto } from './models/office-list-item.dto';
import { OfficeDto } from './models/office.dto';

@Injectable()
export class OfficeRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  getNew(): Observable<OfficeDetailsDto> {
    return this.httpGet(`${this.bp}/offices/init`, OfficeDetailsMapper);
  }

  get(id: ID): Observable<OfficeDetailsDto> {
    return this.httpGet(`${this.bp}/offices/${id}`, optimisticLockingOf(OfficeDetailsMapper));
  }

  create(details: OfficeDetailsDto): Observable<OfficeDetailsDto> {
    return this.httpPost(`${this.bp}/offices`, details, optimisticLockingOf(OfficeDetailsMapper));
  }

  update(details: OfficeDetailsDto): Observable<OfficeDetailsDto> {
    return this.httpPut(`${this.bp}/offices/${details.id}`, details, optimisticLockingOf(OfficeDetailsMapper));
  }

  checkUsers(id: ID): Observable<OfficeListItemDto> {
    return this.httpGet(`${this.bp}/offices/${id}/check-users`, identity());
  }

  deactivate(office: OfficeListItemDto): Observable<OfficeListItemDto> {
    return this.httpPut(
      `${this.bp}/offices/${office.officeId}/deactivate`,
      office,
      emptyOf(optimisticLockingOf(OfficeListItemMapper)),
    );
  }

  delete(office: OfficeListItemDto): Observable<OfficeListItemDto> {
    return this.httpDelete(
      `${this.bp}/offices/${office.officeId}`,
      office,
      emptyOf(optimisticLockingOf(OfficeListItemMapper)),
    );
  }

  findAllAsAutocompleteDataSource(name: string): SlicedDataSource<OfficeListItemDto> {
    return this.createSlicedDataSource({
      method: RequestMethod.Get,
      body: { name },
      url: `${this.bp}/offices?autocomplete=true`,
      responseMapper: arrayOf(OfficeListItemMapper),
    });
  }

  findActiveAndInactiveRecords(officeId: OfficeActiveRecordDto): Observable<OfficeActiveRecordDto> {
    return this.httpPost(
      `${this.bp}/offices/active-inactive-records`, 
      officeId, 
      OfficeActiveRecordMapper,
    );
  }
  
  findAllByMasterZone(masterZoneId: ID, statuses?: string[], types: string[] = ['FRANCHISE', 'TEMPLATE']): Observable<OfficeDto[]> {
    let url = `${this.bp}/master-zones/${masterZoneId}/offices`;
    url += '?';
    if (statuses && statuses.length) {
      statuses.forEach((status, index) => {
        url += `statuses=${status.toUpperCase()}${ index < statuses.length - 1 ? '&' : '' }`;
      });
    }
    if (types && types.length) {
      types.forEach((type, index) => {
        url += `&types=${type.toUpperCase()}`;
      });
    }
    return this.httpGet(url, arrayOf(OfficeMapper))
      .pipe(map$(sortByIgnoreCase('name')));
  }

  findAllAsDataSource(filter?: any): SlicedDataSource<OfficeListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/offices`, filter, identity(),
                                          arrayOf(OfficeListItemMapper));
  }

  findAllForFinderAsDataSource(filter: OfficeFilterDto): SlicedDataSource<OfficeListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/offices/finder`,
      filter,
      OfficeFilterMapper,
      arrayOf(OfficeListItemMapper));
  }

  getVariables<T>(settings: OfficeVariableSet<T>): Observable<T> {
    const notUniqueNames = pickBy(countBy(settings), (count) => count > 1);
    if (!isEmpty(notUniqueNames)) {
      // tslint:disable-next-line:max-line-length
      throw new Error(`OfficeRestService.getVariables: some of requested variables are not unique: ${keys(notUniqueNames).join(
        ', ')}`);
    }

    const varToObjName = mapValues(invertBy(settings, ({ name }) => name), first);
    const responseMapper = entity(fromPairs(values(settings).map(({ name, mapper }) => [name, mapper])));

    return this.httpPost(`${this.bp}/offices/sys-vars`, values(settings).map(({ name }) => name), identity(), responseMapper)
      .pipe(map$((response) => <any>mapKeys(response, (_, varName: string) => varToObjName[varName])));
  }

  activateOffice(officeId: ID): Observable<OfficeDetailsDto> {
    return this.httpPut(`${this.bp}/offices/${officeId}/activate`, undefined, optimisticLockingOf(OfficeDetailsMapper));
  }

  blockOffice(officeId: ID): Observable<OfficeDetailsDto> {
    return this.httpPut(`${this.bp}/offices/${officeId}/block`, undefined, optimisticLockingOf(OfficeDetailsMapper));
  }

  validate(id: ID): Observable<any> {
    return this.httpPost(
      `${this.bp}/offices/${id}/validate`,
      {},
      identity(),
      identity(),
    );
  }
}
